import { useCallback, useMemo, useState } from "react";
import {
  DEFAULT_PAGE_SIZE,
  MenuAnalyticsMealPeriod,
  MenuAnalyticsTimeRange,
  MenuItemsSortField,
  SortDirection,
} from "../types";

// Centralized filter / sort / pagination state for the analytics dashboard.
// Lives above the tabs so selections persist when switching between the Menu
// Items and Ingredients tabs. Any filter change resets to page 1;
export interface AnalyticsFilterState {
  timeRange: MenuAnalyticsTimeRange;
  startDate?: string; 
  endDate?: string;
  mealPeriod: MenuAnalyticsMealPeriod;
  teamId?: string; 
  sortBy: MenuItemsSortField;
  sortDir: SortDirection;
  page: number; 
  pageSize: number;
}

const INITIAL: AnalyticsFilterState = {
  timeRange: "thisWeek",
  mealPeriod: "all",
  sortBy: "totalOrders",
  sortDir: "desc",
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

export interface UseAnalyticsFilters {
  filters: AnalyticsFilterState;
  setTimeRange: (timeRange: MenuAnalyticsTimeRange) => void;
  setCustomRange: (startDate: string, endDate: string) => void;
  setMealPeriod: (mealPeriod: MenuAnalyticsMealPeriod) => void;
  setTeamId: (teamId: string | undefined) => void;
  setSort: (sortBy: MenuItemsSortField) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

export const useAnalyticsFilters = (): UseAnalyticsFilters => {
  const [filters, setFilters] = useState<AnalyticsFilterState>(INITIAL);

  // Filter changes reset pagination; sort/page changes do not.
  const patchAndResetPage = useCallback(
    (patch: Partial<AnalyticsFilterState>) => setFilters(prev => ({ ...prev, ...patch, page: 1 })),
    []
  );

  const setTimeRange = useCallback(
    (timeRange: MenuAnalyticsTimeRange) =>
      patchAndResetPage(
        timeRange === "custom" ? { timeRange } : { timeRange, startDate: undefined, endDate: undefined }
      ),
    [patchAndResetPage]
  );

  const setCustomRange = useCallback(
    (startDate: string, endDate: string) => patchAndResetPage({ timeRange: "custom", startDate, endDate }),
    [patchAndResetPage]
  );

  const setMealPeriod = useCallback((mealPeriod: MenuAnalyticsMealPeriod) => patchAndResetPage({ mealPeriod }), [patchAndResetPage]);
  const setTeamId = useCallback((teamId: string | undefined) => patchAndResetPage({ teamId }), [patchAndResetPage]);

  const setSort = useCallback(
    (sortBy: MenuItemsSortField) =>
      setFilters(prev => ({
        ...prev,
        sortBy,
        sortDir: prev.sortBy === sortBy && prev.sortDir === "desc" ? "asc" : "desc",
        page: 1,
      })),
    []
  );

  const setPage = useCallback((page: number) => setFilters(prev => ({ ...prev, page })), []);
  const setPageSize = useCallback((pageSize: number) => setFilters(prev => ({ ...prev, pageSize, page: 1 })), []);

  return useMemo(
    () => ({ filters, setTimeRange, setCustomRange, setMealPeriod, setTeamId, setSort, setPage, setPageSize }),
    [filters, setTimeRange, setCustomRange, setMealPeriod, setTeamId, setSort, setPage, setPageSize]
  );
};
